import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import {
  ClickAction,
  ClickTargetType,
  TrackerExtraInfo,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { EpisodeViewerHeaderTitle } from 'app-components/viewer/EpisodeViewerHeaderTitle';
import { useTrackingState } from 'contexts/TrackingProvider';
import { ArrowIcon } from 'icons/ArrowIcon';
import React, { ReactNode, useCallback } from 'react';
import { getTextLineStyle } from 'styles/common';
import { Link } from 'ui-components/common/Link';

export interface EpisodeTopNavBarProps {
  contentId?: number;
  contentTitle?: string;
  onClick?: () => void;
  episodeTitle?: string;
  leftButtons?: ReactNode[];
  rightButtons?: ReactNode[];
  color?: string;
  backgroundColor?: string;
  trackerExtraInfo: TrackerExtraInfo;
}

export const EpisodeNavigationBar = ({
  contentId,
  contentTitle,
  onClick,
  episodeTitle,
  leftButtons,
  rightButtons,
  trackerExtraInfo,
  color = Color.Black,
  backgroundColor = Color.White,
}: EpisodeTopNavBarProps) => {
  const { tracker } = useTrackingState();
  const handleTrackClickTitle = useCallback(() => {
    tracker.track(TrackingEventType.Click, {
      action: ClickAction.ToDirittoContentDetail,
      clickType: ClickTargetType.Title,
      trackerExtraInfo,
    });
  }, [tracker, trackerExtraInfo]);

  const handleClick = useCallback(() => {
    handleTrackClickTitle();
    onClick?.();
  }, [handleTrackClickTitle, onClick]);

  return (
    <StyledContainer color={color} backgroundColor={backgroundColor}>
      <EpisodeViewerHeaderTitle leftButtons={leftButtons} rightButtons={rightButtons}>
        <StyledTitle>
          {!episodeTitle && contentId && contentTitle && <StyledContentTitle>{contentTitle}</StyledContentTitle>}
          {episodeTitle &&
            contentId &&
            contentTitle &&
            (onClick ? (
              <>
                <StyledEpisodeTitle>{episodeTitle}</StyledEpisodeTitle>
                <StyledContentTitleWrapper onClick={handleClick}>
                  <StyledContentTitle>{contentTitle}</StyledContentTitle>
                  <StyledArrowIcon direction="right" className="size-[12px]" color={color} />
                </StyledContentTitleWrapper>
              </>
            ) : (
              <>
                <StyledEpisodeTitle>{episodeTitle}</StyledEpisodeTitle>
                <Link href={`/contents/${contentId}`} onClick={handleTrackClickTitle}>
                  <StyledContentTitleWrapper>
                    <StyledContentTitle>{contentTitle}</StyledContentTitle>
                    <StyledArrowIcon direction="right" className="size-[12px]" color={color} />
                  </StyledContentTitleWrapper>
                </Link>
              </>
            ))}
        </StyledTitle>
      </EpisodeViewerHeaderTitle>
    </StyledContainer>
  );
};

const StyledContainer = styled.div(
  ({ backgroundColor, color }: { backgroundColor: string; color: string }) => css`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;

    color: ${color};
    background-color: ${backgroundColor};
  `,
);

const StyledTitle = styled.div`
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const StyledEpisodeTitle = styled.div`
  ${Typography.XSmall};
  font-weight: ${FontWeight.Bold};
  ${getTextLineStyle(1)}
`;

const StyledContentTitleWrapper = styled.div`
  ${Typography.XXSmall};
  font-weight: ${FontWeight.Regular};
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: row;
`;

const StyledContentTitle = styled.div`
  ${getTextLineStyle(1)}
`;

const StyledArrowIcon = styled(ArrowIcon)`
  flex-shrink: 0;
`;
